import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { GalleryImage } from '../../data/gallery';
interface LightboxProps {
  images: GalleryImage[];
  index: number | null;
  onClose: () => void;
  onChange: (index: number) => void;
}
export function Lightbox({
  images,
  index,
  onClose,
  onChange
}: LightboxProps) {
  const total = images.length;
  const go = (step: number) => {
    if (index === null) return;
    onChange((index + step + total) % total);
  };
  useEffect(() => {
    if (index === null) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') onChange((index + 1) % total);
      if (event.key === 'ArrowLeft') onChange((index - 1 + total) % total);
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [index, total, onClose, onChange]);
  const current = index !== null ? images[index] : null;
  const navButton = 'absolute top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full border border-edge bg-coal text-chalk transition-colors duration-150 hover:border-flame hover:bg-flame hover:text-ink';
  return <AnimatePresence data-id="element-172" data-mp="2phbpnl4nbtm0">
      {current && index !== null && <motion.div role="dialog" aria-modal="true" aria-label="Image viewer" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} onClick={onClose} className="fixed inset-0 z-50 flex items-center justify-center bg-ink/95 p-4 md:p-16" data-id="element-173" data-mp="2lht7gx47lwmv">
          <motion.img src={current.src} alt={current.alt} initial={{
        scale: 0.96
      }} animate={{
        scale: 1
      }} exit={{
        scale: 0.96
      }} transition={{
        duration: 0.22,
        ease: [0.23, 1, 0.32, 1]
      }} onClick={event => event.stopPropagation()} className="max-h-[80vh] max-w-full rounded-2xl object-contain" data-id="element-174" data-mp="348u2xb64fa1o" />
          <button type="button" onClick={onClose} aria-label="Close image viewer" autoFocus className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-flame text-ink transition-colors duration-150 hover:bg-chalk" data-id="element-175" data-mp="3q0x1kk9hq9lq">
            <XIcon className="h-5 w-5" data-id="element-176" data-mp="7u8xmxq6fshn" />
          </button>
          <button type="button" aria-label="Previous image" className={`${navButton} left-3 md:left-6`} onClick={event => {
        event.stopPropagation();
        go(-1);
      }} data-id="element-177" data-mp="7us5tmq0z2ih">
            <ChevronLeftIcon className="h-6 w-6" data-id="element-178" data-mp="29mnd7q590kuw" />
          </button>
          <button type="button" aria-label="Next image" className={`${navButton} right-3 md:right-6`} onClick={event => {
        event.stopPropagation();
        go(1);
      }} data-id="element-179" data-mp="2ekghueqwl6t2">
            <ChevronRightIcon className="h-6 w-6" data-id="element-180" data-mp="273a6nstpe7jt" />
          </button>
          <p className="absolute bottom-5 left-1/2 -translate-x-1/2 font-display text-lg font-semibold tracking-[0.2em] text-chalk/70" data-id="element-181" data-mp="229pb07fl1pns">
            <span className="text-flame" data-id="element-182" data-mp="1x91h9dzyyupv">{String(index + 1).padStart(2, '0')}</span> / {String(total).padStart(2, '0')}
          </p>
        </motion.div>}
    </AnimatePresence>;
}